import { useCallback, useId, useState } from 'react'
import { useDataGridConfig } from './useDataGridConfig'
import { PATIENT_EXCLUDED_FILTERS_FROM_POPOVER_VIEW } from '@/services/patients'
import type { GridFilterItemWithId } from '@/lib/stores/filter'

export function doesOperatorRequireValue(operator: string): boolean {
  return operator !== 'isEmpty' && operator !== 'isNotEmpty'
}

interface UseFilteringPopoverOptions {
  tableId: string
}

export default function useFilteringPopover({ tableId }: UseFilteringPopoverOptions) {
  const { handleFiltersChange } = useDataGridConfig({ tableId })
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const idPrefix = useId()
  const createEmptyFilter = (): GridFilterItemWithId => ({
    id: `${idPrefix}-${Date.now()}`,
    field: '',
    operator: 'startsWith',
    value: undefined
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleFiltersChange is stable
  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)

    // Sync with current filters when opening
    handleFiltersChange(filters => (filters.length > 0 ? filters : []))
  }, [])
  const handleClose = () => setAnchorEl(null)

  const handleUpdateFilter = (id: string, updated: GridFilterItemWithId) =>
    handleFiltersChange(prev => prev.map(f => (f.id === id ? updated : f)))

  const handleRemoveFilter = (id: string) =>
    handleFiltersChange(prev => {
      const next = prev.filter(f => f.id !== id)
      return next.length === 0 ? [createEmptyFilter()] : next
    })

  const handleAddFilter = () => handleFiltersChange(prev => [...prev, createEmptyFilter()])

  // biome-ignore lint/correctness/useExhaustiveDependencies: all functions are stable
  const handleClear = useCallback(() => {
    // We want to clear all filters except the ones that are excluded from the popover view
    handleFiltersChange(prev => prev.filter(f => PATIENT_EXCLUDED_FILTERS_FROM_POPOVER_VIEW.includes(f.field)))

    handleClose()
  }, [])

  return {
    anchorEl,
    handleOpen,
    handleUpdateFilter,
    handleRemoveFilter,
    handleAddFilter,
    handleClear,
    handleClose
  }
}
